import { REACT_APP_BACKEND_BASE_URL } from "../configs/config";
import { AuthenticationError, BackendError } from "../types/errors/ApiErrors";

/**
 * What a non-OK response body yielded: the most useful detail string for the
 * error message, the machine-readable reason code when the server sent one, the
 * sentence the server wrote for the person using the app, and the wait a rate
 * limit asked for.
 */
interface ErrorResponsePayload {
  detail?: string;
  reason?: string;
  code?: string;
  serverMessage?: string;
  retryAfterSeconds?: number;
}

/**
 * Reads a non-OK response body and pulls out the most useful detail
 * string for an error message. Tries to parse JSON and use a `message`
 * or `error` field; falls back to the raw text; finally to undefined
 * if nothing readable is there. A `reason` field is carried through
 * untouched, so a caller can map the code to its own wording instead of
 * showing transport text. A string `code` field is carried the same way:
 * it is the contract's machine-readable error code, the only thing a caller
 * may branch on.
 *
 * Only the `message` field becomes `serverMessage`. `error` holds a
 * code and the raw text can be anything a proxy felt like sending, so
 * neither is fit to show to a person.
 *
 * Errors during reading are swallowed — we're already throwing on the
 * caller's behalf, and turning a "couldn't read body" into a hard
 * failure would be worse than just losing the extra context.
 */
const readErrorPayload = async ({ response }: { response: Response }): Promise<ErrorResponsePayload> => {
  try {
    const text = await response.text();
    if (!text) {
      return {};
    }
    try {
      const parsed: unknown = JSON.parse(text);
      if (parsed && typeof parsed === "object") {
        const obj = parsed as Record<string, unknown>;
        const reason = typeof obj.reason === "string" && obj.reason.length > 0 ? obj.reason : undefined;
        const code = typeof obj.code === "string" && obj.code.length > 0 ? obj.code : undefined;
        const retryAfterSeconds = typeof obj.retryAfterSeconds === "number" ? obj.retryAfterSeconds : undefined;
        if (typeof obj.message === "string" && obj.message.length > 0) {
          return {
            detail: obj.message,
            reason: reason,
            code: code,
            serverMessage: obj.message,
            retryAfterSeconds: retryAfterSeconds,
          };
        }
        if (typeof obj.error === "string" && obj.error.length > 0) {
          return { detail: obj.error, reason: reason, code: code, retryAfterSeconds: retryAfterSeconds };
        }
        return { detail: text, reason: reason, code: code, retryAfterSeconds: retryAfterSeconds };
      }
      return { detail: text };
    } catch {
      return { detail: text };
    }
  } catch {
    return {};
  }
};

/**
 * Base service class for authenticated API calls.
 * Provides common methods for GET, POST, PUT requests with automatic error handling.
 * All services making authenticated API calls should extend this class.
 */
export abstract class BaseApiService {
  /**
   * Common fetch method with authentication and error handling.
   * Automatically adds Authorization header and throws appropriate errors for failures.
   *
   * @template T - The expected response type
   * @param endpoint - API endpoint path (e.g., "/user/profile")
   * @param authToken - JWT access token from Auth0
   * @param options - Additional fetch options (method, body, headers, etc.)
   * @returns Promise resolving to the parsed response (or undefined for empty bodies)
   * @throws {AuthenticationError} When the response status is 401 or 403
   * @throws {BackendError} For other HTTP errors or network failures
   */
  protected static async fetchWithAuthAsync<T>(
    endpoint: string,
    authToken: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      const response = await fetch(`${REACT_APP_BACKEND_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
          ...options.headers,
        },
      });

      if (!response.ok) {
        await this.throwForFailedResponseAsync({ response: response });
      }

      return this.parseResponseAsync<T>({ response: response });
    } catch (error) {
      // If it's already our custom error, rethrow it
      if (error instanceof AuthenticationError || error instanceof BackendError) {
        throw error;
      }

      // Network errors (fetch failed entirely)
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new BackendError({
          message: "Network error: Unable to connect to the server",
        });
      }

      // Other unexpected errors
      throw new BackendError({
        message: error instanceof Error ? error.message : "Unknown error occurred",
      });
    }
  }

  /**
   * Turns a non-OK response into the error its status calls for.
   *
   * Reads the body for context first. Server-side 4xx responses typically carry
   * a user-readable `message` (and sometimes `error`) field that's far more
   * useful than just "Bad Request" / "Forbidden" — without it, every UI catch
   * block is forced to log into App Insights to diagnose what the server
   * actually said. Reading before throwing is safe because we don't pass
   * `response` further; the body is single-use.
   *
   * Callers that cannot go through fetchWithAuthAsync — a multipart upload, a
   * file download — come here instead, so every failure carries the same reason
   * code, sentence, and wait a JSON call would.
   *
   * @param params - Named parameters object
   * @param params.response - The non-OK response
   * @throws {AuthenticationError} When the response status is 401 or 403
   * @throws {BackendError} For every other status
   */
  protected static async throwForFailedResponseAsync({ response }: { response: Response }): Promise<never> {
    const errorPayload = await readErrorPayload({ response: response });
    const detail = errorPayload.detail;
    const statusText = response.statusText || "";
    const composed = detail ? `${statusText}: ${detail}`.trim().replace(/^:\s*/, "") : statusText;

    if (response.status === 401 || response.status === 403) {
      throw new AuthenticationError({
        message: `Authentication failed: ${composed}`,
        statusCode: response.status,
      });
    }

    throw new BackendError({
      message: `API request failed: ${composed}`,
      statusCode: response.status,
      reason: errorPayload.reason,
      code: errorPayload.code,
      serverMessage: errorPayload.serverMessage,
      retryAfterSeconds: errorPayload.retryAfterSeconds,
    });
  }

  /**
   * Performs a GET request with authentication
   *
   * @template T - The expected response type
   * @param endpoint - API endpoint path
   * @param authToken - JWT access token from Auth0
   * @returns Promise resolving to the parsed JSON response
   * @throws {AuthenticationError} When authentication fails
   * @throws {BackendError} For other errors
   */
  protected static async getAsync<T>(endpoint: string, authToken: string): Promise<T> {
    return this.fetchWithAuthAsync<T>(endpoint, authToken, { method: "GET" });
  }

  /**
   * Performs a POST request with authentication
   *
   * @template T - The expected response type
   * @param endpoint - API endpoint path
   * @param authToken - JWT access token from Auth0
   * @param body - Optional request body object (will be JSON stringified)
   * @returns Promise resolving to the parsed JSON response
   * @throws {AuthenticationError} When authentication fails
   * @throws {BackendError} For other errors
   */
  protected static async postAsync<T>(endpoint: string, authToken: string, body?: object): Promise<T> {
    return this.fetchWithAuthAsync<T>(endpoint, authToken, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Performs a PUT request with authentication
   *
   * @template T - The expected response type
   * @param endpoint - API endpoint path
   * @param authToken - JWT access token from Auth0
   * @param body - Optional request body object (will be JSON stringified)
   * @returns Promise resolving to the parsed JSON response
   * @throws {AuthenticationError} When authentication fails
   * @throws {BackendError} For other errors
   */
  protected static async putAsync<T>(endpoint: string, authToken: string, body?: object): Promise<T> {
    return this.fetchWithAuthAsync<T>(endpoint, authToken, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Performs a PATCH request with authentication
   *
   * @template T - The expected response type
   * @param endpoint - API endpoint path
   * @param authToken - JWT access token from Auth0
   * @param body - Optional request body object (will be JSON stringified)
   * @returns Promise resolving to the parsed JSON response
   * @throws {AuthenticationError} When authentication fails
   * @throws {BackendError} For other errors
   */
  protected static async patchAsync<T>(endpoint: string, authToken: string, body?: object): Promise<T> {
    return this.fetchWithAuthAsync<T>(endpoint, authToken, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Performs a DELETE request with authentication
   *
   * @template T - The expected response type
   * @param endpoint - API endpoint path
   * @param authToken - JWT access token from Auth0
   * @returns Promise resolving to the parsed JSON response
   * @throws {AuthenticationError} When authentication fails
   * @throws {BackendError} For other errors
   */
  protected static async deleteAsync<T>(endpoint: string, authToken: string): Promise<T> {
    return this.fetchWithAuthAsync<T>(endpoint, authToken, {
      method: "DELETE",
    });
  }

  /**
   * Safely parse an API response and handle empty bodies.
   *
   * @template T - The expected response type
   * @param params - Named parameters object
   * @param params.response - Response instance to parse
   * @returns Parsed body or undefined for empty responses
   */
  private static async parseResponseAsync<T>({ response }: { response: Response }): Promise<T> {
    const statusHasNoBody = response.status === 204 || response.status === 205 || response.status === 304;
    const contentLengthHeader = response.headers.get("content-length");
    const hasKnownLength = contentLengthHeader !== null;
    const hasBody = !statusHasNoBody && (!hasKnownLength || contentLengthHeader !== "0");

    if (!hasBody) {
      return undefined as unknown as T;
    }

    const contentType = response.headers.get("content-type");
    const rawText = await response.text();

    if (rawText.length === 0) {
      return undefined as unknown as T;
    }

    const isJson = typeof contentType === "string" && contentType.includes("application/json");

    if (!isJson) {
      return rawText as unknown as T;
    }

    try {
      return JSON.parse(rawText) as T;
    } catch (error) {
      throw new BackendError({
        message:
          error instanceof Error ? `Failed to parse JSON response: ${error.message}` : "Failed to parse JSON response",
        statusCode: response.status,
      });
    }
  }
}
